import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import html2pdf from 'html2pdf.js';
import quotationDetailsData from '../data/quotation/quotation_details.json';
import technical_specification, {
  recoveryBoothContent,
  pre_treatment_process_steps,
  GEMA_Automatic_System,
} from '../data/quotation/technical_specification.js';
import '../components/quotation/QuotationStyles.css';
import QuotationCoverPage from '../components/quotation/QuotationCoverPage';
import QuotationFromToPage from '../components/quotation/QuotationFromToPage';
import QuotationScopeOfSupplyPage from '../components/quotation/QuotationScopeOfSupplyPage';
import QuotationProductDetailsPages from '../components/quotation/QuotationProductDetailsPages';
import QuotationCommercialOfferPage from '../components/quotation/QuotationCommercialOfferPage';
import TermConditions from '../components/quotation/TermConditions.js';
import { mapClientToQuotationFormat, patchAddresseeSection } from '../utils/clientMapper';

function numberToWords(num) {
  if (!Number.isFinite(num) || num < 0) return '';
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const toWords = (n) => {
    if (n === 0) return '';
    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + toWords(n % 100) : '');
    if (n < 100000) return toWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + toWords(n % 1000) : '');
    if (n < 10000000) return toWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + toWords(n % 100000) : '');
    return toWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + toWords(n % 10000000) : '');
  };
  return toWords(Math.floor(num)) ? `Rupees ${toWords(Math.floor(num))} Only` : '';
}

function formatSubjectFromProducts(products = []) {
  const names = products
    .map((p) => (p?.title || p?.productKey || '').trim())
    .filter(Boolean);

  if (!names.length) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export default function GenerateQuotation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const componentRef = useRef(null);
  const proposalState = location.state?.quotationData;
  const quotationDataRef = useRef(proposalState);
  if (proposalState) quotationDataRef.current = proposalState;
  const showBackToEdit = Boolean(location.state?.quotationData);

  const handleDownloadPDF = useCallback(async () => {
    const element = componentRef.current;
    if (!element) return;

    setIsGenerating(true);
    try {
      const opt = {
        margin: 0,
        filename: 'VACSPL_Techno_Commercial_Proposal.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
          hotfixes: ['px_scaling'],
        },
        pagebreak: {
          mode: ['css', 'legacy'],
          before: '.quotation-page + .quotation-page',
        },
      };

      await html2pdf().set(opt).from(element).save();
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setIsGenerating(false);
    }
  }, []);

  useEffect(() => {
    const base = {
      ...quotationDetailsData,
      company: quotationDetailsData.company || {},
      client: quotationDetailsData.client || {},
      letter: quotationDetailsData.letter || {},
      scopeOfSupply: quotationDetailsData.scopeOfSupply || {},
      sections: [...(quotationDetailsData.sections || [])],
      authorized_signatory: quotationDetailsData.authorized_signatory || {},
    };

    const uiClient = proposalState?.selectedClient;
    if (uiClient) {
      base.client = mapClientToQuotationFormat(uiClient);
    }

    if (proposalState?.products?.length) {
      const dynamicSubjectProducts = formatSubjectFromProducts(proposalState.products);
      if (dynamicSubjectProducts) {
        const dynamicSubject = `Your Requirement of ${dynamicSubjectProducts}`;
        base.letter = {
          ...base.letter,
          subject: dynamicSubject,
        };
        base.metadata = {
          ...(base.metadata || {}),
          subject: dynamicSubject,
        };
      }

      base.plantProposalProducts = proposalState.products;
      base.scopeOfSupply = {
        ...base.scopeOfSupply,
        items: proposalState.products.map((p, i) => ({
          sno: i + 1,
          item: p.title || p.productKey,
          unit: `${p.quantity ?? 1} set(s)`,
        })),
        valueAddition: base.scopeOfSupply?.valueAddition ?? [],
      };

      const commercialOffer = proposalState.commercialOffer;
      const commercialSection = base.sections.find((s) => s.title === 'COMMERCIAL OFFER');

      if (commercialSection) {
        if (commercialOffer?.groups?.length) {
          commercialSection.commercialOfferGroups = commercialOffer.groups;
          commercialSection.pricing = {
            grand_total: commercialOffer.grandTotal,
            grand_total_in_words: numberToWords(commercialOffer.grandTotal),
            groupBreakup: commercialOffer.groups.map((g, i) => ({
              label: `Group ${i + 1}`,
              total: g.subtotal,
              in_words: numberToWords(g.subtotal),
            })),
          };
        } else {
          const priceRows = proposalState.products.map((p, i) => {
            const qty = p.quantity ?? 1;
            const unitPrice = Number(p.price) || 0;
            return {
              item: i + 1,
              description: p.title || p.productKey,
              quantity: `${qty} set(s)`,
              price: unitPrice * qty,
            };
          });
          const grandTotal = priceRows.reduce((sum, r) => sum + (r.price || 0), 0);
          commercialSection.tables = [{ table_type: 'price_breakup', rows: priceRows }];
          commercialSection.pricing = {
            ...commercialSection.pricing,
            grand_total: grandTotal,
            grand_total_in_words: numberToWords(grandTotal),
          };
        }
      }
      if (uiClient) {
        base.sections = patchAddresseeSection(base.sections, uiClient);
      }
    }
    setData(base);
  }, [proposalState]);

  const commercialSection = data?.sections?.find((s) => s.title === 'COMMERCIAL OFFER') || {};
  const termsSection = data?.sections?.find((s) => s.title === 'Terms & conditions') || {};
  const signatory = data?.authorized_signatory || {};

  if (!data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const handleBackToEdit = () => {
    const qd = quotationDataRef.current || {};
    navigate('/plant-proposal', {
      state: {
        returnToEdit: true,
        quotationData: {
          products: (qd.products || data?.plantProposalProducts) ?? [],
          commercialOffer: qd.commercialOffer || null,
          selectedProductKey: qd.selectedProductKey || null,
          selectedClient: qd.selectedClient ?? null,
        },
      },
    });
  };

  return (
    <Box sx={{ pb: 10 }}>
      <Box
        className="quotation-no-print"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, sm: '260px' },
          right: 0,
          zIndex: 99,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          py: 1.5,
          px: 2,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 -1px 10px rgba(0, 0, 0, 0.08)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          {showBackToEdit && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToEdit}
              sx={{ textTransform: 'none' }}
            >
              Back to Edit
            </Button>
          )}
          <Typography variant="h5" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
            Generate Quotation
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <GetAppIcon />}
          onClick={handleDownloadPDF}
          disabled={isGenerating}
          sx={{
            bgcolor: '#ffbe0b',
            color: '#000814',
            '&:hover': { bgcolor: '#e6ab0a' },
            boxShadow: 3,
            borderRadius: 2,
            ml: 'auto',
          }}
        >
          {isGenerating ? 'Generating PDF...' : 'Download as PDF'}
        </Button>
      </Box>

      <div ref={componentRef} className="quotation-root quotation-pdf-source">
        <QuotationCoverPage
          company={data.company}
          metadata={{ subject: data.letter?.subject || data.metadata?.subject, reference: data.letter?.reference }}
        />
        <QuotationFromToPage company={data.company} client={data.client} letter={data.letter} />
        <QuotationScopeOfSupplyPage scopeOfSupply={data.scopeOfSupply} />
        <QuotationCommercialOfferPage
          commercialSection={commercialSection}
          termsSection={termsSection}
          signatory={signatory}
        />
        <QuotationProductDetailsPages
          sections={data.sections}
          technicalSpecification={technical_specification}
          recoveryBoothContent={recoveryBoothContent}
          preTreatmentSteps={pre_treatment_process_steps}
          gemaAutomaticSystem={GEMA_Automatic_System}
          selectedProducts={data.plantProposalProducts}
        />

        <TermConditions
          commercialSection={commercialSection}
          termsSection={termsSection}
          signatory={signatory}
        />
      </div>
    </Box>
  );
}
